import { SegmentStrategy } from "../rules/intoSegments";
import { RuleSet } from "../rules/ruleSet";

/**
 * Americanist phonetic notation: used by some linguists for native American & European languages
 * http://wieldoc.org/wp-content/uploads/2016/05/WIELD-Americanist-Symbols.pdf
 * https://en.wikipedia.org/wiki/Americanist_phonetic_notation
 */
export const americanistToIpa: RuleSet = {
  preprocess: ['lowerCase'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Ipa,

  sortRulesByInputLength: true,
  rules: [
    // plosives
    { from: 'p', to: 'p' },
    { from: 'b', to: 'b' },
    { from: 't̯', to: 't̪' },
    { from: 'd̯', to: 'd̪' },
    { from: 't', to: 't' },
    { from: 'd', to: 'd' },
    { from: 'ṭ', to: 'ʈ' },
    { from: 'ḍ', to: 'ɖ' },
    { from: 'k̯', to: 'c' },
    { from: 'kʸ', to: 'c' }, // variant
    { from: 'ɡ̯', to: 'ɟ' },
    { from: 'gʸ', to: 'ɟ' }, // variant
    { from: 'k', to: 'k' },
    { from: 'g', to: 'g' },
    { from: 'q', to: 'q' },
    { from: 'ɡ̇', to: 'ɢ' },
    { from: 'ʔ', to: 'ʔ' },
    // implosives
    { from: 'ɓ̥', to: 'ɓ̥' },
    { from: 'ɓ', to: 'ɓ' },
    { from: 'ɗ̥', to: 'ɗ̥' },
    { from: 'ɗ', to: 'ɗ' },
    { from: 'ʄ̥', to: 'ʄ̥' },
    { from: 'ʄ', to: 'ʄ' },
    { from: 'ɠ̊', to: 'ɠ̥' },
    { from: 'ɠ', to: 'ɠ' },
    { from: 'ʛ̥', to: 'ʛ̥' },
    { from: 'ʛ', to: 'ʛ' },
    // affricates
    { from: 'pf', to: 'p͡f' },
    { from: 'tθ', to: 't̪͡θ' },
    { from: 'dð', to: 'd̪͡ð' },
    { from: 'c̯', to: 't̪͡s̪' },
    { from: 'dz̯', to: 'd̪͡z̪' },
    { from: 'c', to: 't͡s' },
    { from: 'dz', to: 'd͡z' },
    { from: 'c̣', to: 'ʈ͡ʂ' },
    { from: 'dẓ', to: 'ɖ͡ʐ' },
    { from: 'č', to: 't͡ʃ' },
    { from: 'ǰ', to: 'd͡ʒ' },
    { from: 'ć', to: 't͡ɕ' },
    { from: 'dź', to: 'd͡ʑ' },
    { from: 'kx', to: 'k͡x' },
    { from: 'qx̣', to: 'q͡χ' },
    // fricatives
    { from: 'ɸ', to: 'ɸ' },
    { from: 'β', to: 'β' },
    { from: 'f', to: 'f' },
    { from: 'v', to: 'v' },
    { from: 'θ', to: 'θ' },
    { from: 'ð', to: 'ð' },
    { from: 's', to: 's' },
    { from: 'z', to: 'z' },
    { from: 's̯', to: 's̪' },
    { from: 'z̯', to: 'z̪' },
    { from: 'ṣ', to: 'ʂ' },
    { from: 'ẓ', to: 'ʐ' },
    { from: 'ś', to: 'ɕ' },
    { from: 'ź', to: 'ʑ' },
    { from: 'š', to: 'ʃ' },
    { from: 'ž', to: 'ʒ' },
    { from: 'x̯', to: 'ç' },
    { from: 'ɣ̯', to: 'ʝ' },
    { from: 'x', to: 'x' },
    { from: 'ɣ', to: 'ɣ' },
    { from: 'x̣', to: 'χ' },
    { from: 'ɣ̇', to: 'ʁ' },
    { from: 'ħ', to: 'ħ' },
    { from: 'ʕ', to: 'ʕ' },
    { from: 'h', to: 'h' },
    { from: 'ɦ', to: 'ɦ' },
    // nasals
    { from: 'm̥', to: 'm̥' },
    { from: 'm', to: 'm' },
    { from: 'n̯̊', to: 'n̥' }, // needs refinement
    { from: 'n̯', to: 'n̪' },
    { from: 'n̥', to: 'n̥' },
    { from: 'n', to: 'n' },
    { from: 'ṇ̊', to: 'ɳ̥' },
    { from: 'ṇ', to: 'ɳ' },
    { from: 'ń', to: 'ȵ' },  // alveopalatal -> palato alveolar
    { from: 'ñ̥', to: 'ɲ̥' },
    { from: 'ñ', to: 'ɲ' },
    { from: 'ŋ̊', to: 'ŋ̥' },
    { from: 'ŋ', to: 'ŋ' },
    { from: 'ŋ̇', to: 'ɴ' },
    // lateral fricative
    { from: 'ł', to: 'ɬ' },
    { from: 'ɮ', to: 'ɮ' },
    // lateral approximant
    { from: 'l̯', to: 'l̪' },
    { from: 'l̥', to: 'l̥' },
    { from: 'l', to: 'l' },
    { from: 'ḷ', to: 'ɭ' },
    { from: 'łʸ', to: 'ʎ' },
    { from: 'lʸ', to: 'ʎ' },
    // lateral affricates
    { from: 'ƛ', to: 't͡ɬ' },
    { from: 'λ', to: 'd͡ɮ' },
    // trill
    { from: 'r̥̃', to: 'r̥' },
    { from: 'r̃', to: 'r' },
    { from: 'ʀ', to: 'ʀ' },
    // tap
    { from: 'r̯', to: 'ɾ̪' },
    { from: 'r', to: 'ɾ' },
    { from: 'ṛ', to: 'ɽ' },
    // approximant
    { from: 'ʋ', to: 'ʋ' },
    { from: 'ʍ', to: 'ʍ' }, // labial-velar glide -> labial-velar fricative
    { from: 'w̥', to: 'ʍ' },
    { from: 'w', to: 'w' },
    { from: 'ɹ̥', to: 'ɹ̥' },
    { from: 'ɹ', to: 'ɹ' },
    { from: 'ɹ̣', to: 'ɻ' },
    { from: 'y', to: 'j' },
    { from: 'ẉ', to: 'ɰ' },

    // vowels
    // high: higher
    { from: 'i', to: 'i' },
    { from: 'ü', to: 'y' },
    { from: 'ɨ', to: 'ɨ' },
    { from: 'ʉ', to: 'ʉ' },
    { from: 'ï', to: 'ɯ' },
    { from: 'u', to: 'u' },
    // high: lower
    { from: 'ɪ', to: 'ɪ' },
    { from: 'ʊ̈', to: 'ʏ' },
    { from: 'ᵻ', to: 'ɘ' },
    { from: 'ʊ̇', to: 'ɵ' },
    { from: 'ʊ', to: 'ʊ' },
    // mid: higher
    { from: 'e', to: 'e' },
    { from: 'ö', to: 'ø' },
    { from: 'ə', to: 'ə' },
    { from: 'ȯ', to: 'ɞ' },  // ?
    { from: 'ë', to: 'ɤ' },
    { from: 'o', to: 'o' },
    // mid: lower
    { from: 'ɛ', to: 'ɛ' },
    { from: 'ɔ̈', to: 'œ' },
    { from: 'ɛ̇', to: 'ɜ' },
    { from: 'ɔ̇', to: 'ɞ' },  // ?
    { from: 'ʌ', to: 'ʌ' },
    { from: 'ɔ', to: 'ɔ' },
    // low: higher
    { from: 'æ', to: 'æ' },
    { from: 'æ̇', to: 'ɐ' },
    // low: lower
    { from: 'a', to: 'a' },
    { from: 'ɒ̈', to: 'ɶ' },
    { from: 'ɑ', to: 'ɑ' },
    { from: 'ɒ', to: 'ɒ' },

    // Americanist stress symbols - acute=ˈ & grave=ˌ
    // https://en.wikipedia.org/wiki/Americanist_phonetic_notation
    // upper ascii
    // (put stress after vowel to match the diacritic case, fixed up by stressRule)
    { from: 'à', to: 'aˌ' }, { from: 'á', to: 'aˈ' },
    { from: 'è', to: 'eˌ' }, { from: 'é', to: 'eˈ' },
    { from: 'ì', to: 'iˌ' }, { from: 'í', to: 'iˈ' },
    { from: 'ò', to: 'oˌ' }, { from: 'ó', to: 'oˈ' },
    { from: 'ù', to: 'uˌ' }, { from: 'ú', to: 'uˈ' },

    // diacritics
    { from: ':', to: 'ː' }, { from: '꞉', to: 'ː' },
    { from: 'ʸ', to: 'ʲ' }, // palatalized
    { from: '\u0301', to: 'ˈ' }, // primary stress, e.g., /v́/
    { from: '\u0300', to: 'ˌ' }, // secondary stress, e.g., /v̀/
    { from: '\u032f', to: '\u031F' }, // fronting, e.g. /c̯/
    { from: '\u0323', to: '\u0320' }, // retracted, e.g. /c̣/
    { from: '\u0313', to: 'ˀ' },      // glottalized, e.g. /c̓/
    { from: '\u0328', to: '\u0303' }, // nasalized, e.g. /v̨/

    //
    // randomness from cleanupIpa
    //

    { from: '\u0313', to: 'ʲ' },  // or add a /z/
    { from: '′', to: 'ʲ' },
    { from: '\u0315', to: 'ˀ' },
  ],

  stressRule: { default: 0, move: 'beforeVowel' },
}
